import React, { useState, useEffect, useContext } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { AppContext } from './App.js';
import { ArrowLeft, ShieldCheck, Lock, Check, Loader2, CreditCard, Tag, X, AlertCircle } from 'lucide-react';

// Your existing test key preserved
const stripePromise = loadStripe('pk_test_51S35vBKHy89O5Qxfm2d1dZiuwEt8n56dDeKCK1CuhBToTpEkZzqC8abjElD7wOifFDzbFikNidfsBxorTAW9ROaq00POXlqxle');

// --- SUB-COMPONENT: Promo Code Input (Refyled for Studio Look) ---
const PromoCodeInput = ({ onPromoApplied, planDetails, originalPrice, setOrderSummary }) => {
    const [promoCode, setPromoCode] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [promoStatus, setPromoStatus] = useState(null); 
    const [appliedPromo, setAppliedPromo] = useState(null);
    const { appInstance } = useContext(AppContext);

    const validatePromoCode = async () => {
        if (!promoCode.trim()) return;
        setIsValidating(true);
        setPromoStatus(null);

        try {
            const functions = getFunctions(appInstance);
            const validatePromo = httpsCallable(functions, 'validatePromoCode');
            const result = await validatePromo({ 
                code: promoCode.trim().toUpperCase(),
                planType: planDetails.id 
            });
            
            if (result.data.valid) {
                const discount = result.data.discount;
                const discountAmount = Math.round(originalPrice * (discount.percent_off / 100));
                const newPrice = originalPrice - discountAmount;
                
                const promoData = {
                    code: promoCode.trim().toUpperCase(),
                    discountPercent: discount.percent_off,
                    discountAmount: discountAmount,
                    newPrice: newPrice,
                    description: discount.name || `${discount.percent_off}% off`
                };

                setAppliedPromo(promoData);
                setPromoStatus('valid');
                onPromoApplied(promoData);
                
                setOrderSummary({
                    originalPrice: originalPrice,
                    discountAmount: discountAmount,
                    finalPrice: newPrice,
                    promoCode: promoCode.trim().toUpperCase(),
                    promoDescription: discount.name
                });
            } else {
                setPromoStatus('invalid');
                setAppliedPromo(null);
                onPromoApplied(null);
                setOrderSummary(null);
            }
        } catch (error) {
            console.error('Promo validation error:', error);
            setPromoStatus('invalid');
        } finally {
            setIsValidating(false);
        }
    };

    const removePromoCode = () => {
        setPromoCode('');
        setPromoStatus(null);
        setAppliedPromo(null);
        onPromoApplied(null);
        setOrderSummary(null);
    };

    return (
        <div className="space-y-3 pt-4 border-t border-white/5">
            <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Discount Code</label>
            <div className="flex space-x-2">
                <div className="relative flex-1">
                    <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="ENTER CODE"
                        disabled={appliedPromo || isValidating}
                        className="w-full bg-zinc-950/50 border border-zinc-800 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-zinc-600 transition-all placeholder:text-zinc-700 font-mono uppercase"
                    />
                    {promoStatus === 'invalid' && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <X className="w-4 h-4 text-red-500" />
                        </div>
                    )}
                    {promoStatus === 'valid' && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Check className="w-4 h-4 text-green-500" />
                        </div>
                    )}
                </div>
                
                {!appliedPromo ? (
                    <button
                        onClick={validatePromoCode}
                        disabled={!promoCode.trim() || isValidating}
                        className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                        {isValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </button>
                ) : (
                    <button
                        onClick={removePromoCode}
                        className="px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-400 text-sm font-medium rounded-lg transition-colors"
                    >
                        Remove
                    </button>
                )}
            </div>
            
            {appliedPromo && (
                <div className="flex justify-between items-center text-sm bg-green-500/10 border border-green-500/20 px-3 py-2 rounded text-green-400">
                    <span>{appliedPromo.code} applied</span>
                    <span className="font-mono">-${appliedPromo.discountAmount}</span>
                </div>
            )}
        </div>
    );
};

// --- SUB-COMPONENT: Payment Form (Refyled for Studio Look) ---
const PaymentForm = ({ planDetails, purchaseIntent, auth, appInstance, showMessageBox, setCurrentPage, appliedPromo, orderSummary }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsProcessing(true);

        const isFreeUpgrade = orderSummary?.finalPrice === 0;

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("Authentication error. Please sign in again.");

            // 1. FREE UPGRADE FLOW
            if (isFreeUpgrade) {
                const functions = getFunctions(appInstance);
                const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
                const renewProjectId = localStorage.getItem('renewProjectId') || null;
                const result = await createPaymentIntent({ 
                    plan: purchaseIntent,
                    promoCode: appliedPromo.code,
                    discountAmount: appliedPromo.discountAmount,
                    renewProjectId: renewProjectId
                });
                
                if (result.data.isFreeUpgrade) {
                    localStorage.removeItem('renewProjectId');
                    await currentUser.getIdToken(true); // Refresh claims
                    setCurrentPage('projects');
                    return;
                }
            }

            // 2. STANDARD PAYMENT FLOW
            if (!stripe || !elements) return;

            const functions = getFunctions(appInstance);
            const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
            const renewProjectId = localStorage.getItem('renewProjectId') || null;

            const result = await createPaymentIntent({ 
                plan: purchaseIntent,
                ...(renewProjectId && { renewProjectId }),
                ...(appliedPromo && {
                    promoCode: appliedPromo.code,
                    discountAmount: appliedPromo.discountAmount
                })
            });

            const { clientSecret } = result.data;
            const cardElement = elements.getElement(CardElement);

            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: { email: currentUser.email },
                },
            });

            if (error) throw error;

            if (paymentIntent.status === 'succeeded') {
                if (renewProjectId) localStorage.removeItem('renewProjectId');
                await currentUser.getIdToken(true); // Refresh claims
                setCurrentPage('projects');
            }

        } catch (error) {
            console.error('Payment Processing Error:', error);
            showMessageBox("Transaction Failed", error.message, "error");
        } finally {
            setIsProcessing(false);
        }
    };

    const isFreeUpgrade = orderSummary?.finalPrice === 0;

    return (
        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {!isFreeUpgrade && (
                <div className="space-y-3">
                    <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Card Details</label>
                    <div className="bg-zinc-950/50 border border-zinc-800 p-4 rounded-lg transition-colors focus-within:border-zinc-600 focus-within:ring-1 focus-within:ring-zinc-600">
                        <CardElement
                            options={{
                                style: {
                                    base: {
                                        fontSize: '15px',
                                        color: '#ffffff',
                                        '::placeholder': { color: '#52525b' },
                                        backgroundColor: 'transparent',
                                        fontFamily: 'system-ui, sans-serif',
                                    },
                                    invalid: { color: '#ef4444' },
                                },
                            }}
                        />
                    </div>
                </div>
            )}

            <button
                type="submit"
                disabled={(!stripe && !isFreeUpgrade) || isProcessing}
                className="w-full bg-white text-black hover:bg-zinc-200 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 mt-8 disabled:opacity-50"
            >
                {isProcessing ? (
                    <>
                        <Loader2 className="animate-spin w-5 h-5" />
                        <span>Processing...</span>
                    </>
                ) : isFreeUpgrade ? (
                    'Activate Free Account'
                ) : (
                    `Pay $${orderSummary?.finalPrice ?? planDetails.price.replace('$','')}`
                )}
            </button>
            
            <div className="flex items-center justify-center gap-2 text-zinc-600 text-[10px] uppercase tracking-widest mt-4">
                <Lock size={10} />
                <span>SSL Encrypted Transaction</span>
            </div>
        </form>
    );
};

// --- MAIN COMPONENT ---
const ImprovedCheckoutPage = () => {
    const { auth, appInstance, purchaseIntent, setCurrentPage, showMessageBox } = useContext(AppContext);
    const [planDetails, setPlanDetails] = useState(null);
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [orderSummary, setOrderSummary] = useState(null);

    // Initial Logic (Preserved)
    useEffect(() => {
        const isTrialUser = localStorage.getItem('trialUser') === 'true';
        
        if (purchaseIntent === 'demo') {
            if (isTrialUser) setCurrentPage('projects');
            else setCurrentPage('signup');
            return;
        }
        
        if (!purchaseIntent) {
            setCurrentPage('signup');
            return;
        }
        
        const plans = {
            indie: {
                id: 'indie',
                name: 'Indie Project',
                price: '$199',
                originalPrice: 299,
                features: ['1080p Export', 'H.264 / .mp4', 'No Watermark', '90 Days Storage']
            },
            pro: {
                id: 'pro',
                name: 'Feature Film',
                price: '$399',
                originalPrice: 599,
                features: ['4K Export', 'ProRes 4444 + Alpha', 'NLE Data Export', 'Priority Support']
            }
        };

        if (plans[purchaseIntent]) {
            setPlanDetails(plans[purchaseIntent]);
        } else {
            setCurrentPage('signup');
        }
    }, [purchaseIntent, setCurrentPage]);

    if (!planDetails) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500">Loading...</div>;

    // Calculate integers for logic
    const basePrice = parseInt(planDetails.price.replace('$', ''));

    return (
        <Elements stripe={stripePromise}>
            <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans selection:bg-purple-500/30 selection:text-purple-200 flex flex-col">
                
                {/* Header */}
                <header className="px-6 py-6 border-b border-white/5 bg-zinc-950/50 backdrop-blur-md sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto flex items-center justify-between">
                        <button 
                            onClick={() => setCurrentPage('signup')}
                            className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-sm font-medium"
                        >
                            <ArrowLeft size={16} />
                            Back to Plans
                        </button>
                        <div className="text-xl font-medium tracking-tight">
                            BLACKBOX<span className="text-zinc-600">cr</span>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 flex items-center justify-center p-6 relative">
                    {/* Background Light */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-900/5 blur-[120px] rounded-full pointer-events-none"></div>

                    <div className="w-full max-w-5xl grid md:grid-cols-2 gap-12 relative z-10">
                        
                        {/* LEFT: Manifest / Summary */}
                        <div className="space-y-6">
                            <div>
                                <h1 className="text-3xl font-semibold mb-2">Checkout</h1>
                                <p className="text-zinc-400">Finalize your project configuration.</p>
                            </div>

                            <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6">
                                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-4">Item Details</p>
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h2 className="text-xl font-bold text-white">{planDetails.name}</h2>
                                        <p className="text-zinc-500 text-sm mt-1">Single Project License</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xl font-bold text-white">{planDetails.price}</p>
                                    </div>
                                </div>
                                <div className="space-y-3 pt-6 border-t border-white/5">
                                    {planDetails.features.map((feat, i) => (
                                        <div key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                                            <Check size={14} className="text-white" />
                                            {feat}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Payment Actions */}
                        <div className="bg-zinc-900 border border-white/10 rounded-2xl p-8 shadow-2xl h-fit">
                            <h3 className="text-lg font-medium text-white mb-6">Payment Method</h3>
                            
                            {/* Order Totals */}
                            <div className="space-y-3 mb-6 text-sm">
                                <div className="flex justify-between text-zinc-400">
                                    <span>Subtotal</span>
                                    <span>${basePrice}</span>
                                </div>
                                {orderSummary?.discountAmount > 0 && (
                                    <div className="flex justify-between text-green-400">
                                        <span>Discount</span>
                                        <span>-${orderSummary.discountAmount}</span>
                                    </div>
                                )}
                                <div className="h-px bg-white/10 my-2"></div>
                                <div className="flex justify-between text-white font-bold text-lg">
                                    <span>Total Due</span>
                                    <span>${orderSummary ? orderSummary.finalPrice : basePrice}</span>
                                </div>
                            </div>

                            {/* Promo Input */}
                            <PromoCodeInput 
                                onPromoApplied={setAppliedPromo}
                                planDetails={planDetails}
                                originalPrice={basePrice}
                                setOrderSummary={setOrderSummary}
                            />

                            {/* Payment Form */}
                            <PaymentForm 
                                planDetails={planDetails}
                                purchaseIntent={purchaseIntent}
                                auth={auth}
                                appInstance={appInstance}
                                showMessageBox={showMessageBox}
                                setCurrentPage={setCurrentPage}
                                appliedPromo={appliedPromo}
                                orderSummary={orderSummary}
                            />
                        </div>

                    </div>
                </main>
            </div>
        </Elements>
    );
};

export default ImprovedCheckoutPage;